import React, { useState, useEffect, useRef } from 'react';
import { chavesAPI, categoriasAPI } from '../services/api';
import Modal from '../components/Modal';
import './Chaves.css';

function Chaves() {
  const [chaves, setChaves] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingChave, setViewingChave] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterFaixa, setFilterFaixa] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});
  const [editingResults, setEditingResults] = useState(false);
  const [currentResults, setCurrentResults] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCampeonato, setFilterCampeonato] = useState('');
  const chavePreviewRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [chavesRes, categoriasRes] = await Promise.all([
        chavesAPI.getAll(),
        categoriasAPI.getAll()
      ]);

      setChaves(chavesRes.data);
      setCategorias(categoriasRes.data);
      setLoading(false);
    } catch (error) {
      console.error('❌ [LOAD] Erro ao carregar chaves:', error);
      setLoading(false);
    }
  };

  const handleVisualizarHtml = async (chave) => {
    try {
      const response = await chavesAPI.getHtml(chave.id);
      setHtmlContent(response.data);
      setViewingChave(chave);
      
      // Trata resultados de forma segura
      let resultados = {};
      try {
        if (typeof chave.resultados === 'string') {
          resultados = chave.resultados ? JSON.parse(chave.resultados) : {};
        } else if (typeof chave.resultados === 'object' && chave.resultados !== null) {
          resultados = chave.resultados;
        }
      } catch (e) {
        console.warn('Erro ao processar resultados, usando objeto vazio:', e);
        resultados = {};
      }
      
      setCurrentResults(resultados);
      setEditingResults(false);
    } catch (error) {
      console.error('Erro ao visualizar chave:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro ao Visualizar Chave',
        message: 'Não foi possível carregar a visualização da chave.\nTente novamente em alguns instantes.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleBaixarPdf = async (chave) => {
    try {
      // A rota /chaves/:id/pdf exige login de admin — abrir a URL direto
      // numa aba nova não manda o token e sempre retornava "Não autenticado".
      // Buscando via chavesAPI (com o Bearer já anexado) e abrindo o HTML
      // recebido como blob resolve isso sem expor a rota publicamente.
      const response = await chavesAPI.getPdf(chave.id);
      const blob = new Blob([response.data], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro ao Gerar PDF',
        message: 'Não foi possível gerar o arquivo PDF.\nVerifique sua conexão e tente novamente.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleExcluir = async (id) => {
    const chave = chaves.find(c => c.id === id);
    const categoriaNome = chave?.categoria_nome || 'Desconhecida';
    const status = getChaveStatus(chave);
    const finalizada = status === 'finalizada';

    // Chave finalizada já tem resultado registrado — exclusão é irreversível
    // e some com o histórico da categoria, então o aviso é mais enfático.
    // A autorização real já é o login de admin (adminRequired na API); uma
    // segunda senha hardcoded no front era só teatro de segurança.
    setModalConfig({
      type: 'confirm',
      title: finalizada ? '⚠️ Excluir Chave Finalizada' : 'Confirmar Exclusão de Chave',
      message: finalizada
        ? `ATENÇÃO: Esta chave já tem resultados registrados!\n\nChave ${chave?.numero_chave}\nCategoria: ${categoriaNome}\nAtletas: ${chave?.num_atletas}\n\nExcluir apaga o histórico de resultados dessa categoria. Esta ação não poderá ser desfeita.`
        : `Deseja realmente excluir a Chave ${chave?.numero_chave}?\nCategoria: ${categoriaNome}\nAtletas: ${chave?.num_atletas}\n\nEsta ação não poderá ser desfeita.`,
      showCancel: true,
      onConfirm: async () => {
        try {
          await chavesAPI.delete(id);
          loadData();
          setModalConfig({
            type: 'success',
            title: 'Sucesso',
            message: 'Chave excluída com sucesso!',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        } catch (error) {
          console.error('Erro ao excluir chave:', error);
          setModalConfig({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao excluir chave. Tente novamente.',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        }
      }
    });
    setModalOpen(true);
  };

  const getCategoriaNome = (chave) => {
    return chave.categoria_nome || 'Desconhecida';
  };

  // Determina o status da chave baseado nos resultados
  const getChaveStatus = (chave) => {
    if (!chave) return 'nao_iniciada';
    
    let resultados = {};
    try {
      if (typeof chave.resultados === 'string') {
        resultados = chave.resultados ? JSON.parse(chave.resultados) : {};
      } else if (typeof chave.resultados === 'object' && chave.resultados !== null) {
        resultados = chave.resultados;
      }
    } catch (e) {
      resultados = {};
    }

    const numResultados = Object.keys(resultados).length;
    
    // Sem resultados = não iniciou
    if (numResultados === 0) {
      return 'nao_iniciada';
    }

    // Calcular total de lutas (excluir lutas com BYE)
    let rodadas = [];
    try {
      if (typeof chave.rodadas === 'string') {
        rodadas = JSON.parse(chave.rodadas);
      } else if (Array.isArray(chave.rodadas)) {
        rodadas = chave.rodadas;
      }
    } catch (e) {
      rodadas = [];
    }

    let totalLutas = 0;
    rodadas.forEach(rodada => {
      if (rodada && rodada.lutas && Array.isArray(rodada.lutas)) {
        // Contar apenas lutas válidas (sem BYE)
        rodada.lutas.forEach(luta => {
          if (!luta) return;
          
          const atleta1Str = typeof luta.atleta1 === 'string' ? luta.atleta1 : (luta.atleta1?.nome || '');
          const atleta2Str = typeof luta.atleta2 === 'string' ? luta.atleta2 : (luta.atleta2?.nome || '');
          
          const temBye = atleta1Str.includes('BYE') || atleta2Str.includes('BYE');
          
          if (!temBye) {
            totalLutas++;
          }
        });
      }
    });

    // Se tem resultados >= total de lutas válidas, está finalizada
    if (totalLutas > 0 && numResultados >= totalLutas) {
      return 'finalizada';
    }

    // Caso contrário, está em andamento
    return 'em_andamento';
  };

  const handleFecharVisualizacao = () => {
    setViewingChave(null);
    setHtmlContent('');
    setEditingResults(false);
    setCurrentResults({});
  };

  const handleMarcarVencedor = (rodadaIndex, lutaIndex, atletaIndex) => {
    const key = `rodada_${rodadaIndex}_luta_${lutaIndex}`;
    setCurrentResults(prev => ({
      ...prev,
      [key]: atletaIndex
    }));
  };

  const handleSalvarResultados = async () => {
    try {
      await chavesAPI.updateResultados(viewingChave.id, currentResults);

      // Recarregar a chave atualizada para mostrar vencedores avançados
      await chavesAPI.getById(viewingChave.id);

      // Recarregar lista de chaves para atualizar status
      await loadData();

      // Fechar a visualização e voltar para lista
      setViewingChave(null);
      setHtmlContent('');
      setEditingResults(false);
      setCurrentResults({});
      
      // Mostrar modal de sucesso
      setModalConfig({
        type: 'success',
        title: 'Sucesso',
        message: 'Resultados salvos com sucesso!\n\nOs vencedores foram avançados para a próxima rodada.',
        onConfirm: () => {
          setModalOpen(false);
        }
      });
      setModalOpen(true);
    } catch (error) {
      console.error('❌ [SALVAR] Erro ao salvar resultados:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro',
        message: `Erro ao salvar resultados.\n\n${error.response?.data?.message || error.message || 'Tente novamente.'}`,
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  // Adiciona interatividade ao HTML quando entrar em modo de edição
  useEffect(() => {
    if (!editingResults || !chavePreviewRef.current || !viewingChave) return;

    const container = chavePreviewRef.current;
    const lutasElements = container.querySelectorAll('.luta');
    
    lutasElements.forEach((lutaEl, globalIndex) => {
      const atletas = lutaEl.querySelectorAll('.atleta:not(.bye)');
      
      // Encontrar a rodada e índice da luta
      let rodadaIndex = 0;
      let lutaIndex = 0;
      
      const rodadas = container.querySelectorAll('.rodada');
      rodadas.forEach((rodada, rIdx) => {
        const lutasNaRodada = rodada.querySelectorAll('.luta');
        lutasNaRodada.forEach((luta, lIdx) => {
          if (luta === lutaEl) {
            rodadaIndex = rIdx;
            lutaIndex = lIdx;
          }
        });
      });

      const key = `rodada_${rodadaIndex}_luta_${lutaIndex}`;
      const vencedorIndex = currentResults[key];

      atletas.forEach((atletaEl, atletaIndex) => {
        // Remove listeners anteriores
        const newEl = atletaEl.cloneNode(true);
        atletaEl.parentNode.replaceChild(newEl, atletaEl);
        
        // Adiciona classe de selecionável
        newEl.style.cursor = 'pointer';
        newEl.style.transition = 'all 0.3s';
        
        // Marca vencedor se já selecionado
        if (vencedorIndex === atletaIndex) {
          newEl.classList.add('vencedor-selecionado');
          newEl.style.background = 'linear-gradient(135deg, #ffd89b 0%, #fdcb6e 100%)';
          newEl.style.fontWeight = 'bold';
          newEl.style.borderLeft = '5px solid #f39c12';
        } else {
          newEl.classList.remove('vencedor-selecionado');
          newEl.style.background = '';
          newEl.style.fontWeight = '';
          newEl.style.borderLeft = '';
        }
        
        // Hover effect
        newEl.addEventListener('mouseenter', () => {
          if (vencedorIndex !== atletaIndex) {
            newEl.style.background = '#f0f4ff';
            newEl.style.transform = 'translateX(5px)';
          }
        });
        
        newEl.addEventListener('mouseleave', () => {
          if (vencedorIndex !== atletaIndex) {
            newEl.style.background = '';
            newEl.style.transform = '';
          }
        });
        
        // Click handler
        newEl.addEventListener('click', () => {
          handleMarcarVencedor(rodadaIndex, lutaIndex, atletaIndex);
        });
      });
    });
  }, [editingResults, currentResults, viewingChave]);

  // Filtrar chaves com base na busca
  const chavesFiltradas = chaves.filter(chave => {
    const categoria = getCategoriaNome(chave);
    const matchSearch = !searchTerm ||
      chave.numero_chave.toString().includes(searchTerm) ||
      categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (chave.faixa && chave.faixa.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (chave.campeonato_nome && chave.campeonato_nome.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchCategoria = !filterCategoria || chave.categoria_id === filterCategoria;
    const matchFaixa = !filterFaixa || chave.faixa === filterFaixa;
    const matchStatus = !filterStatus || getChaveStatus(chave) === filterStatus;
    const matchCampeonato = !filterCampeonato || chave.campeonato_id === filterCampeonato;

    return matchSearch && matchCategoria && matchFaixa && matchStatus && matchCampeonato;
  });

  // Obter faixas únicas das chaves
  const faixasUnicas = [...new Set(chaves.map(c => c.faixa).filter(Boolean))];

  // Obter campeonatos únicos das chaves (id + nome, sem duplicar)
  const campeonatosUnicos = Object.values(
    chaves.reduce((acc, c) => {
      if (c.campeonato_id) acc[c.campeonato_id] = { id: c.campeonato_id, nome: c.campeonato_nome || 'Sem nome' };
      return acc;
    }, {})
  );

  if (loading) return <div className="loading">Carregando...</div>;

  if (viewingChave) {
    return (
      <div>
        <div className="page-header">
          <h1>Visualização - {viewingChave.codigo_chave ? `#${viewingChave.codigo_chave}` : `Chave ${viewingChave.numero_chave}`}</h1>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={handleFecharVisualizacao}>
              ← Voltar
            </button>
            {!editingResults ? (
              <button 
                className="btn btn-primary" 
                onClick={() => setEditingResults(true)}
              >
                ✏️ Editar Resultados
              </button>
            ) : (
              <>
                <button className="btn btn-success" onClick={handleSalvarResultados}>
                  💾 Salvar Resultados
                </button>
                <button className="btn btn-danger" onClick={() => {
                  setEditingResults(false);
                  // Restaura resultados originais
                  let resultados = {};
                  try {
                    if (typeof viewingChave.resultados === 'string') {
                      resultados = viewingChave.resultados ? JSON.parse(viewingChave.resultados) : {};
                    } else if (typeof viewingChave.resultados === 'object' && viewingChave.resultados !== null) {
                      resultados = viewingChave.resultados;
                    }
                  } catch (e) {
                    console.warn('Erro ao restaurar resultados:', e);
                    resultados = {};
                  }
                  setCurrentResults(resultados);
                }}>
                  ✕ Cancelar
                </button>
              </>
            )}
          </div>
        </div>
        {editingResults && (
          <div className="editing-mode-banner">
            <strong>Modo de Edição:</strong> Clique no atleta vencedor de cada luta para marcá-lo
          </div>
        )}
        <div 
          ref={chavePreviewRef}
          className={`chave-preview ${editingResults ? 'editing-mode' : ''}`}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Chaves Geradas</h1>
        <p>Visualize e gerencie as chaves de competição</p>
      </div>

      {chaves.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3>Nenhuma chave gerada ainda</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Comece gerando chaves para as categorias cadastradas
          </p>
          <a href="/gerar-chaves" className="btn btn-success">
            Gerar Chaves
          </a>
        </div>
      ) : (
        <>
          <div className="search-filters-container">
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Buscar por número da chave, categoria ou faixa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
            
            <div className="filters-row">
              <select
                value={filterCampeonato}
                onChange={(e) => setFilterCampeonato(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos os Campeonatos</option>
                {campeonatosUnicos.map(camp => (
                  <option key={camp.id} value={camp.id}>{camp.nome}</option>
                ))}
              </select>

              <select
                value={filterCategoria}
                onChange={(e) => setFilterCategoria(e.target.value)}
                className="filter-select"
              >
                <option value="">Todas as Categorias</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nome}</option>
                ))}
              </select>

              <select
                value={filterFaixa}
                onChange={(e) => setFilterFaixa(e.target.value)}
                className="filter-select"
              >
                <option value="">Todas as Faixas</option>
                {faixasUnicas.map(faixa => (
                  <option key={faixa} value={faixa}>{faixa}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos os Status</option>
                <option value="nao_iniciada">○ Não Iniciadas</option>
                <option value="em_andamento">▶ Em Andamento</option>
                <option value="finalizada">✓ Finalizadas</option>
              </select>

              {(searchTerm || filterCategoria || filterFaixa || filterStatus || filterCampeonato) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategoria('');
                    setFilterFaixa('');
                    setFilterStatus('');
                    setFilterCampeonato('');
                  }}
                >
                  Limpar Filtros
                </button>
              )}
            </div>

            <div className="pagination">
              <span>Mostrando <strong>{chavesFiltradas.length}</strong> de <strong>{chaves.length}</strong> chaves</span>
            </div>
          </div>

          {chavesFiltradas.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h3>Nenhuma chave encontrada</h3>
              <p style={{ color: '#666' }}>
                Tente ajustar os filtros de busca
              </p>
            </div>
          ) : (
            <div className="chaves-grid">
              {chavesFiltradas.map(chave => {
                const status = getChaveStatus(chave);
                return (
            <div key={chave.id} className={`chave-card chave-${status}`}>
              <div className="chave-header event-tile-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', padding: '15px' }}>
                {chave.codigo_chave && <span className="chave-codigo">#{chave.codigo_chave}</span>}
                <span className="chave-badge">Chave de {chave.tamanho}</span>
                <div className="chave-status-tag" style={{ marginLeft: 'auto' }}>
                  {status === 'finalizada' && '✓ FINALIZADA'}
                  {status === 'em_andamento' && '▶ EM ANDAMENTO'}
                  {status === 'nao_iniciada' && '○ NÃO INICIADA'}
                </div>
              </div>
              
              <div className="chave-info">
                <div className="info-row">
                  <span className="label">Campeonato:</span>
                  <span className="value">{chave.campeonato_nome || '—'}</span>
                </div>

                <div className="info-row">
                  <span className="label">Categoria:</span>
                  <span className="value">{getCategoriaNome(chave)}</span>
                </div>

                {chave.faixa && (
                  <div className="info-row">
                    <span className="label">Faixa:</span>
                    <span className="value">{chave.faixa}</span>
                  </div>
                )}
                
                <div className="info-row">
                  <span className="label">Atletas:</span>
                  <span className="value">{chave.num_atletas}</span>
                </div>
                
                <div className="info-row">
                  <span className="label">Data:</span>
                  <span className="value">
                    {new Date(chave.data_geracao).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              <div className="chave-actions">
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => handleVisualizarHtml(chave)}
                >
                  👁️ Visualizar
                </button>
                <button 
                  className="btn btn-success btn-sm" 
                  onClick={() => handleBaixarPdf(chave)}
                >
                  📄 Baixar PDF
                </button>
                <button 
                  className="btn btn-danger btn-sm" 
                  onClick={() => handleExcluir(chave.id)}
                >
                  🗑️ Excluir
                </button>
              </div>
            </div>
                );
              })}
        </div>
          )}
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        {...modalConfig}
      />
    </div>
  );
}

export default Chaves;
